// Pending requirements panel — at-a-glance view of every client with
// outstanding items on file. Renders on Home so the operator opens the
// CRM and immediately sees who's stuck where, with a one-click link to
// edit that client's checklist.
//
// Auto-hides when nobody has anything pending. When >7-day-old items
// exist, the whole panel turns red so it can't be ignored.
//
// Reads public.client_requirements + public.clients via RLS. The
// daily cron (api/workflow/scan-requirements.js) handles the actual
// reminders — this is just the visibility surface.

import React from 'react';
import { supabase } from '../lib/supabase';

const DAY_MS = 86_400_000;

function ageDays(iso) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 0;
  return Math.floor((Date.now() - t) / DAY_MS);
}

function ageBucket(days) {
  if (days <= 3)  return { label: 'Fresh',   tone: 'slate' };
  if (days <= 7)  return { label: 'Aging',   tone: 'amber' };
  return                  { label: 'Overdue', tone: 'red'   };
}

export default function PendingRequirementsPanel({ currentUser, onOpenClient = () => {} }) {
  const [rows, setRows]       = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      // Pull every pending requirement for this owner (RLS-scoped),
      // then group client-side by client_id. One query keeps it cheap.
      const { data: reqs } = await supabase
        .from('client_requirements')
        .select('client_id, kind, description, created_at, last_reminder_at, reminder_count, ai_followup_enabled, due_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: true });

      const byClient = new Map();
      for (const r of (reqs || [])) {
        let g = byClient.get(r.client_id);
        if (!g) { g = { clientId: r.client_id, items: [] }; byClient.set(r.client_id, g); }
        g.items.push(r);
      }

      if (byClient.size === 0) { setRows([]); return; }

      // Hydrate client names in one round-trip.
      const ids = Array.from(byClient.keys());
      const { data: clients } = await supabase
        .from('clients')
        .select('id, name, carrier, policy, phone, email')
        .in('id', ids);
      const clientById = new Map((clients || []).map(c => [c.id, c]));

      const out = [];
      for (const g of byClient.values()) {
        const c = clientById.get(g.clientId);
        if (!c) continue;
        const oldestDays = Math.max(...g.items.map(it => ageDays(it.created_at)));
        const followupOn = g.items.some(it => it.ai_followup_enabled);
        const lastReminderAt = g.items.reduce((acc, it) => {
          if (!it.last_reminder_at) return acc;
          if (!acc) return it.last_reminder_at;
          return Date.parse(it.last_reminder_at) > Date.parse(acc) ? it.last_reminder_at : acc;
        }, null);
        out.push({
          clientId: g.clientId,
          client: c,
          count: g.items.length,
          oldestDays,
          bucket: ageBucket(oldestDays),
          followupOn,
          lastReminderAt,
        });
      }
      // Sort oldest first.
      out.sort((a, b) => b.oldestDays - a.oldestDays);
      setRows(out);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  React.useEffect(() => { load(); }, [load]);

  if (loading || rows.length === 0) return null;

  const overdueCount = rows.filter(r => r.bucket.tone === 'red').length;

  return (
    <div className={`bg-white rounded-xl overflow-hidden ${overdueCount > 0 ? 'border-2 border-red-300 ring-1 ring-red-100' : 'border border-slate-200'}`}>
      <div className={`px-5 py-4 border-b ${overdueCount > 0 ? 'bg-red-50 border-red-100' : 'bg-slate-50 border-slate-100'}`}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className={`text-sm font-bold uppercase tracking-tight ${overdueCount > 0 ? 'text-red-900' : 'text-slate-900'}`}>
              📋 Outstanding requirements
            </h3>
            <div className={`text-xs mt-0.5 ${overdueCount > 0 ? 'text-red-800' : 'text-slate-600'}`}>
              {rows.length} client{rows.length === 1 ? '' : 's'} with pending items
              {overdueCount > 0 && ` · ${overdueCount} overdue 7+ days`}
            </div>
          </div>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {rows.slice(0, 20).map(r => (
          <div key={r.clientId} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-slate-900 truncate flex items-baseline gap-2">
                {r.client.name}
                {!r.followupOn && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 border border-slate-200 rounded-full px-1.5 py-0.5">
                    AI silenced
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 truncate">
                {r.count} item{r.count === 1 ? '' : 's'} pending
                <span className={`ml-1 font-semibold text-${r.bucket.tone}-700`}>
                  · oldest {r.oldestDays}d ({r.bucket.label})
                </span>
                {r.lastReminderAt && (
                  <span className="ml-1 text-slate-500">
                    · AI last touched {new Date(r.lastReminderAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => onOpenClient(r.client)}
                className={`bg-${r.bucket.tone}-600 hover:bg-${r.bucket.tone}-700 text-white text-xs font-bold uppercase tracking-tight px-3 py-1.5 rounded-md`}
                title="Open client to view + manage outstanding items"
              >
                Open client
              </button>
            </div>
          </div>
        ))}
        {rows.length > 20 && (
          <div className="px-5 py-2 text-xs text-slate-500 text-center">
            …and {rows.length - 20} more.
          </div>
        )}
      </div>
    </div>
  );
}
